import React, { useRef, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import Navbar from './Navbar';
import HealthStatusStrip from '../health/HealthStatusStrip';
import { AriaHelper } from '../../utils/accessibility';
import './MainLayout.css';

const MainLayout: React.FC = () => {
  const mainContentRef = useRef<HTMLElement>(null);

  // Keyboard navigation handler
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Alt + M to focus main navigation
      if (event.altKey && event.key === 'm') {
        event.preventDefault();
        const firstNavLink = document.querySelector('.nav-link') as HTMLElement;
        if (firstNavLink) {
          firstNavLink.focus();
          AriaHelper.announce('Main navigation focused', 'polite');
        }
      }
      
      // Alt + C to focus main content
      if (event.altKey && event.key === 'c') {
        event.preventDefault();
        if (mainContentRef.current) {
          mainContentRef.current.focus();
          AriaHelper.announce('Main content focused', 'polite');
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Skip link handler
  const handleSkipToContent = (event: React.MouseEvent | React.KeyboardEvent) => {
    if ('key' in event && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    if (mainContentRef.current) {
      mainContentRef.current.focus();
      AriaHelper.announce('Skipped to main content', 'polite');
    }
  };

  return (
    <div className="main-layout">
      {/* Skip to content link for screen readers */}
      <a
        href="#main-content"
        className="skip-link"
        onClick={handleSkipToContent}
        onKeyDown={handleSkipToContent}
      >
        Skip to main content
      </a>

      {/* New horizontal navbar */}
      <Navbar />

      {/* System Health Status Strip */}
      <HealthStatusStrip />

      {/* Main Content Area */}
      <main 
        id="main-content"
        ref={mainContentRef}
        className="main-content"
        role="main"
        aria-label="Main content area"
        tabIndex={-1}
      >
        <Outlet />
      </main>
    </div>
  );
};

export default MainLayout;
